import React from 'react';
import axios from 'axios';
import { notification } from 'antd';

import { ACTION_TYPES as SECTION_ACTION_TYPES } from '../redux/sections/constants';
import { ACTION_TYPES as PROJECTS_ACTION_TYPES } from '../redux/projects/constants';
import {
  addTaskCustomFieldAction,
  deleteCustomFieldFromProject,
  deleteTaskCustomFieldAction,
  updateTaskCustomFieldAction,
} from '../redux/tasks/actions';

import { UnauthorizedHandler } from '../js/helper/UnauthorizedHandler';
import { isValidObjectId } from '../js/helper';
import { socketSendAction } from '../redux/state-areas/socket/actions';

const baseUrl = process.env.REACT_APP_API_URL;

const config = {
  headers: { Authorization: '' },
};

export const getProjects = (callback) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.get(`${baseUrl}/projects`, config);
      dispatch({
        type: PROJECTS_ACTION_TYPES.GET_USER_PROJECTS_REQUEST_SUCCESS, //'get-users-projects',
        payload: res?.data?.data,
      });

      callback && callback(res?.data?.data);
    } catch (error) {
      const status = error?.response?.status;
      dispatch({
        type: 'error',
        payload: error,
      });
      return status;
    }
  };
};

export const getProjectById = (id, callback, skipActions) => {
  return async (dispatch) => {
    try {
      if (!id || !isValidObjectId(id)) return;
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.get(
        `${baseUrl}/projects/${id}?members=true`,
        config,
      );
      if (!skipActions && res?.data?.status !== 'Not Found') {
        dispatch({
          type: 'get-projects',
          payload: res?.data?.data,
        });
        dispatch({
          type: PROJECTS_ACTION_TYPES.UPDATE_PROJECT_REQUEST_SUCCESS,
          payload: res?.data?.data,
        });
        dispatch({
          type: SECTION_ACTION_TYPES.INITIALIZE_SECTION_QUERY_STATE,
          payload: res?.data?.data?.sections,
        });
      }
      callback && callback(res?.data?.data);
    } catch (error) {
      const status = error?.response?.status;
      notification.error({
        message:
          status === 403
            ? 'You do not have access to this project'
            : 'Something went wrong',
        placement: 'bottomRight',
      });
      dispatch({
        type: 'error',
        payload: error,
      });
      return status;
    }
  };
};

export const editProject = (data, callback) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.put(`${baseUrl}/projects/edit`, data, config);

      // Use the response data directly instead of refetching the entire project
      if (res?.data?.data) {
        dispatch({
          type: PROJECTS_ACTION_TYPES.UPDATE_PROJECT_REQUEST_SUCCESS,
          payload: res?.data?.data,
        });
        dispatch({
          type: 'update-workspace-project',
          payload: res?.data?.data,
        });
      }

      await dispatch({
        type: 'update-pin-project-settings',
        payload: res?.data?.data,
      });

      if (data?.access) {
        notification.success({
          message: `Project set to ${data?.access === 'public' ? 'public' : 'private'}`,
          placement: 'bottomRight',
        });
      }

      callback && callback();
      return res?.data?.data;
    } catch (error) {
      const status = error?.response?.status;
      notification.error({
        message:
          status === 403
            ? 'You do not have access to this project'
            : 'Something went wrong',
        placement: 'bottomRight',
      });
      dispatch({
        type: 'error',
        payload: error,
      });
      return status;
    }
  };
};

export const getProjectTemplates = () => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.get(`${baseUrl}/projects/templates`, config);
      dispatch({
        type: 'set-project-templates',
        payload: res?.data?.data,
      });
    } catch (error) {
      dispatch({
        type: 'error',
        payload: error,
      });
    }
  };
};

export const addProject = (data = {}, callback) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      if (!data?.workspaceId) {
        data.workspace = sessionStorage.getItem('workspaceId');
      }
      const res = await axios.post(`${baseUrl}/projects`, data, config);

      dispatch({
        type: PROJECTS_ACTION_TYPES.ADD_PROJECT_REQUEST_SUCCESS, //'add-project',
        payload: res?.data?.data,
      });

      notification.success({
        message: 'You just added a new project',
        placement: 'bottomRight',
      });

      callback && callback(res?.data?.data);
      return res?.data?.data;
    } catch (error) {
      dispatch({
        type: 'workspace-error',
        payload: error,
      });
      notification.error({
        message: error?.response?.data?.message || 'Oops! something went wrong',
        placement: 'bottomRight',
      });
    }
  };
};

export const updateProjectUsers = (data, callback) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      await axios.post(`${baseUrl}/projects/updateProjectUsers`, data, config);
      callback && callback();
      notification.success({
        message: 'Project roles have been updated',
        placement: 'bottomRight',
      });
      dispatch({
        type: 'updateRolesInProject',
        payload: data,
      });
    } catch (e) {
      notification.error({
        message: e?.message || 'Cannot update role!',
        placement: 'bottomRight',
      });
    }
  };
};

export const duplicateProject = (data, callback) => {
  return async (dispatch) => {
    try {
      dispatch(socketSendAction('duplicate-project', data));
      // config.headers.Authorization = `Bearer ${localStorage.getItem("token")}`;
      // const res = await axios.post(`${baseUrl}/projects/duplicate`, data, config);
      callback && callback();
      notification.success({
        message: 'Project duplication initiated',
        placement: 'bottomRight',
      });
    } catch (e) {
      notification.error({
        message: e?.message || 'Cannot duplicate project!',
        placement: 'bottomRight',
      });
    }
  };
};

export const unsetActiveProject = () => {
  return {
    type: 'unset-active-project',
  };
};

const pendingProjectDeletes = new Map();

export const deleteProject = (id) => {
  return async (dispatch, getState) => {
    const store = getState();
    const projectToDelete =
      store?.workspaceReducer?.workspaceProjects?.find(p => p._id === id) ||
      store?.projectReducer?.myProjects?.find(p => p._id === id);

    const notifKey = `delete-project-${id}`;
    let undone = false;

    // Optimistically remove from UI immediately
    dispatch({ type: 'delete-project', payload: id });
    dispatch({ type: PROJECTS_ACTION_TYPES.DELETE_PROJECT_REQUEST_SUCCESS, payload: id });

    const doDelete = async () => {
      if (undone) return;
      pendingProjectDeletes.delete(id);
      try {
        config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
        await axios.delete(`${baseUrl}/projects/${id}`, config);
      } catch (error) {
        dispatch({ type: 'error', payload: error });
        if (projectToDelete) {
          dispatch({ type: 'restore-project', payload: projectToDelete });
        }
        notification.error({ message: 'Oops! something went wrong', placement: 'bottomRight' });
      }
    };

    notification.success({
      key: notifKey,
      message: (
        <span>
          Deleted <strong>{projectToDelete?.title || 'project'}</strong>
        </span>
      ),
      description: (
        <button
          onClick={() => {
            undone = true;
            clearTimeout(pendingProjectDeletes.get(id));
            pendingProjectDeletes.delete(id);
            notification.destroy(notifKey);
            if (projectToDelete) {
              dispatch({ type: 'restore-project', payload: projectToDelete });
            }
          }}
          style={{
            marginTop: 4,
            background: 'none',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            padding: '3px 12px',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 600,
            color: '#374151',
          }}
        >
          Undo
        </button>
      ),
      duration: 5,
      placement: 'bottomRight',
    });

    pendingProjectDeletes.set(id, setTimeout(doDelete, 5000));
  };
};

export const editPreSection = (data, project) => {
  const newSection = project.sections.map((section) => {
    if (section._id === data.section) {
      section.tasks.push(data._id);
    }
    if (section._id === data.preSection) {
      const index = section.tasks.indexOf(data._id);
      section.tasks.splice(index, 1);
    }
    return section;
  });
  project.sections = newSection;
  return {
    type: 'pre-section-update',
    payload: project,
  };
};

export const updateProjectPreferences = (data, callback) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.post(`${baseUrl}/projects/user`, data, config);
      // Preference update doesn't change project data — no need to refetch
      notification.success({
        message: `Project notifications ${res.data?.data?.notifications ? 'enabled' : 'muted'}`,
        placement: 'bottomRight',
      });

      callback && callback();
      return res?.data?.data;
    } catch (error) {
      dispatch({
        type: 'workspace-error',
        payload: error,
      });
      notification.error({
        message: 'Oops! something went wrong',
        placement: 'bottomRight',
      });
    }
  };
};

export const updateSectionTasksCount = (from, to, taskId, projectId) => {
  return (dispatch) => {
    dispatch({
      type: 'update-section-count',
      payload: {
        from,
        to,
        taskId,
        projectId,
      },
    });
  };
};

export const updateProjectSections = (sections, projectId) => {
  return (dispatch) => {
    dispatch({
      type: 'update-project-sections',
      payload: {
        projectId,
        sections,
      },
    });
  };
};

export const getProjectCustomField = (projectId, callback) => {
  return async (dispatch) => {
    try {
      if (!projectId) return;
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.get(
        `${baseUrl}/customField/project/${projectId}`,
        config,
      );
      dispatch({
        type: 'set-project-custom-fields',
        payload: res?.data?.data,
      });
      callback && callback();
    } catch (error) {
      dispatch({
        type: 'error',
        payload: error,
      });
    }
  };
};

export const uploadFormLogo = (data, callBack) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.post(`${baseUrl}/forms/upload`, data, config);

      callBack && callBack();
      return res.data?.data || {};
    } catch (error) {
      UnauthorizedHandler(dispatch, error);
      dispatch({
        type: 'form-logo-upload-error',
        payload: error,
      });
    }
  };
};

export const submitForm = (formId, data, showNotifications = true) => {
  return async (dispatch) => {
    try {
      if (!formId) return;
      config.headers.Authorization =
        localStorage.getItem('token') &&
        `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.post(
        `${baseUrl}/form/${formId}/submit`,
        data,
        config,
      );

      if (showNotifications) {
        notification.success({
          message: 'Form Submitted!',
          placement: 'bottomRight',
        });
      }

      return res?.data?.data;
    } catch (error) {
      if (showNotifications) {
        notification.error({
          message: 'Something went wrong!',
          placement: 'bottomRight',
        });
      }

      dispatch({
        type: 'error',
        payload: error,
      });
      throw error; // Re-throw so calling code can handle it
    }
  };
};

export const getFormById = (formId) => {
  return async (dispatch) => {
    try {
      if (!formId) return;
      config.headers.Authorization =
        localStorage.getItem('token') &&
        `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.get(`${baseUrl}/form/${formId}/public`, config);

      return res?.data?.data;
    } catch (error) {
      dispatch({
        type: 'error',
        payload: error,
      });
    }
  };
};

export const getProjectForms = (projectId) => {
  return async (dispatch) => {
    try {
      if (!projectId) return;
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.get(
        `${baseUrl}/form/project/${projectId}`,
        config,
      );

      return res?.data?.data;
    } catch (error) {
      dispatch({
        type: 'error',
        payload: error,
      });
    }
  };
};

export const addProjectForm = (data) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.post(`${baseUrl}/form/add`, data, config);

      notification.success({
        message: 'Form Added!',
        placement: 'bottomRight',
      });

      return res?.data?.data;
    } catch (error) {
      dispatch({
        type: 'error',
        payload: error,
      });
    }
  };
};

export const deleteForm = (formId) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      await axios.delete(`${baseUrl}/form/${formId}/delete`, config);

      notification.success({
        message: 'Form Deleted!',
        placement: 'bottomRight',
      });
      return true;
    } catch (error) {
      const status = error?.response?.status;
      notification.error({
        message: status === 403 ? 'Permission denied' : 'Failed to delete form',
        description: status === 403 ? 'You do not have permission to delete this form.' : 'Something went wrong. Please try again.',
        placement: 'bottomRight',
      });
      dispatch({
        type: 'error',
        payload: error,
      });
      return false;
    }
  };
};

export const updateProjectForm = (data) => {
  return async (dispatch) => {
    const formId = data?._id;
    if (!formId) return;
    config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
    const res = await axios.put(
      `${baseUrl}/form/${formId}/update`,
      data,
      config,
    );
    return res?.data?.data;
  };
};

export const addCustomFieldToWorkspace = (data, callback) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.post(`${baseUrl}/customField`, data, config);
      dispatch({
        type: 'add-workspace-field',
        payload: res?.data?.data,
      });
      callback && callback();
      return res?.data?.data;
    } catch (error) {
      console.log('----->>>> error', error);
      dispatch({
        type: 'workspace-error',
        payload: error,
      });
      notification.error({
        message: error?.response?.data?.message || 'Oops! something went wrong',
        placement: 'bottomRight',
      });
    }
  };
};

export const addExistingCustomFieldToProject = (data, callback) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.post(`${baseUrl}/customField/add`, data, config);
      dispatch(getProjectById(data?.entityId));
      callback && callback(res?.data);
      notification.success({
        message: 'Custom Field Added!',
        placement: 'bottomRight',
      });
      return res?.data?.data;
    } catch (error) {
      dispatch({
        type: 'workspace-error',
        payload: error,
      });
    }
  };
};

export const promoteFieldToWorkspace = (customFieldId, workspaceId, callback) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.post(`${baseUrl}/customField/add`, { customFieldId, entityId: workspaceId, entityType: 'workspace' }, config);
      dispatch({ type: 'add-workspace-field', payload: res?.data?.data });
      callback && callback(res?.data?.data);
    } catch (error) {
      dispatch({ type: 'workspace-error', payload: error });
      notification.error({ message: error?.response?.data?.message || 'Oops! something went wrong', placement: 'bottomRight' });
    }
  };
};

export const addCustomFieldToProject = (data, callback) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.post(`${baseUrl}/customField`, data, config);
      dispatch(getProjectById(data?.entityId));
      callback && callback(res?.data?.data);

      dispatch(addTaskCustomFieldAction(res?.data?.data, data?.entityId));

      if (data?.addToWorkspace) {
        dispatch({
          type: 'add-workspace-field',
          payload: res?.data?.data,
        });
      }

      notification.success({
        message: 'Custom Field Added!',
        placement: 'bottomRight',
      });
      return res?.data?.data;
    } catch (error) {
      console.log('----->>>> error', error);
      dispatch({
        type: 'workspace-error',
        payload: error,
      });
      notification.error({
        message: error?.response?.data?.message || 'Oops! something went wrong',
        placement: 'bottomRight',
      });
    }
  };
};

export const updateWorkspaceCustomField = (customFieldId, data, callback) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.put(
        `${baseUrl}/customField/${customFieldId}`,
        data,
        config,
      );
      callback && callback();
      return res;
    } catch (error) {
      dispatch({
        type: 'workspace-error',
        payload: error,
      });
      notification.error({
        message: error?.response?.data?.message || 'Oops! something went wrong',
        placement: 'bottomRight',
      });
    }
  };
};

export const updateProjectCustomField = (customFieldId, data, callback) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.put(
        `${baseUrl}/customField/${customFieldId}`,
        data,
        config,
      );
      dispatch(getProjectById(data?.entityId));
      callback && callback();

      dispatch(updateTaskCustomFieldAction(res?.data?.data));

      notification.success({
        message: 'Custom Field Updated!',
        placement: 'bottomRight',
      });
      return res;
    } catch (error) {
      dispatch({
        type: 'workspace-error',
        payload: error,
      });
      notification.error({
        message: error?.response?.data?.message || 'Oops! something went wrong',
        placement: 'bottomRight',
      });
    }
  };
};

export const deleteProjectCustomField = (
  customFieldId,
  projectId,
  callback,
  intl,
) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.delete(
        `${baseUrl}/customField/project/${projectId}/${customFieldId}`,
        config,
      );

      // dispatch(getProjectById(data?.entityId));
      dispatch(deleteTaskCustomFieldAction(customFieldId));
      dispatch(deleteCustomFieldFromProject(customFieldId));

      callback && callback();
      notification.success({
        message: intl.formatMessage({
          id: 'CUSTOM_FIELD_DELETED',
          defaultMessage: 'Custom Field Deleted!',
        }),
        placement: 'bottomRight',
      });
      return res;
    } catch (error) {
      dispatch({
        type: 'workspace-error',
        payload: error,
      });
      notification.error({
        message: 'Oops! something went wrong',
        placement: 'bottomRight',
      });
    }
  };
};

export const deleteWorkspaceCustomField = (
  customFieldId,
  workspaceId,
  callback,
  intl,
) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.delete(
        `${baseUrl}/customField/workspace/${workspaceId}/${customFieldId}`,
        config,
      );
      // dispatch(getProjectById(data?.entityId));
      callback && callback();
      dispatch({
        type: 'delete-workspace-field',
        payload: customFieldId,
      });
      dispatch(deleteTaskCustomFieldAction(customFieldId));

      notification.success({
        message: intl.formatMessage({
          id: 'CUSTOM_FIELD_DELETED',
          defaultMessage: 'Custom Field Deleted!',
        }),
        placement: 'bottomRight',
      });
      return res;
    } catch (error) {
      dispatch({
        type: 'workspace-error',
        payload: error,
      });
      notification.error({
        message: 'Oops! something went wrong',
        placement: 'bottomRight',
      });
    }
  };
};

export const leaveProject = (data) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      await axios.post(`${baseUrl}/projects/leave`, data, config);
      dispatch({
        type: 'delete-project',
        payload: data?.projectId || '',
      });
      notification.success({
        message: 'Project Left',
        placement: 'bottomRight',
      });
    } catch (error) {
      dispatch({
        type: 'error',
        payload: error,
      });

      notification.error({
        message: error?.response?.data?.message || 'Oops! something went wrong',
        placement: 'bottomRight',
      });
    }
  };
};

export const removeMemberFromProject = (data, callback) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.post(
        `${baseUrl}/projects/removeMembers`,
        data,
        config,
      );
      notification.success({
        message: 'Member removed from project!',
        placement: 'bottomRight',
      });
      if (callback) callback();
      return res.data;
    } catch (error) {
      dispatch({
        type: 'error',
        payload: error,
      });
    }
  };
};

export const joinProject = (data, callback) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.post(`${baseUrl}/projects/join`, data, config);

      if (res?.data?.data?.status != 'pending') {
        await dispatch({
          type: PROJECTS_ACTION_TYPES.ADD_PROJECT_REQUEST_SUCCESS, //'add-project',
          payload: res?.data?.data,
        });
      }

      notification.success({
        message: res?.data?.message || 'Project Joined!',
        placement: 'bottomRight',
      });

      callback && callback(res?.data?.data);
      return res?.data?.data;
    } catch (error) {
      dispatch({
        type: 'error',
        payload: error,
      });

      notification.error({
        message:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to Join Project',
        placement: 'bottomRight',
      });
    }
  };
};

export const setProjectLoading = (isLoading, callback) => {
  return async (dispatch) => {
    try {
      dispatch({
        type: 'projects-loading',
        payload: isLoading,
      });
      callback && callback();
    } catch (error) {
      dispatch({
        type: 'error',
        payload: error,
      });
    }
  };
};

export const approveAccessRequest = (request, callback) => {
  return async (dispatch) => {
    try {
      const requestId = request?._id;
      const entity = request?.entityType + 's';
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.post(
        `${baseUrl}/${entity}/approve`,
        { requestId },
        config,
      );

      notification.success({
        message: 'Access Request Approved!',
        placement: 'bottomRight',
      });

      callback && callback();
      return res?.data?.data;
    } catch (error) {
      dispatch({
        type: 'error',
        payload: error,
      });
    }
  };
};

export const denyAccessRequest = (request, callback) => {
  return async (dispatch) => {
    try {
      const requestId = request?._id;
      const entity = request?.entityType + 's';

      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.post(
        `${baseUrl}/${entity}/decline`,
        { requestId },
        config,
      );

      notification.success({
        message: 'Access Request Denied!',
        placement: 'bottomRight',
      });

      callback && callback();
      return res?.data?.data;
    } catch (error) {
      dispatch({
        type: 'error',
        payload: error,
      });
    }
  };
};

export const updateProject = (data, callback) => {
  return async (dispatch) => {
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.put(`${baseUrl}/projects/edit`, data, config);
      await dispatch({
        type: PROJECTS_ACTION_TYPES.UPDATE_PROJECT_REQUEST_SUCCESS, //'project-update-success',
        payload: res?.data?.data,
      });
      await dispatch({
        type: 'update-workspace-project',
        payload: res?.data?.data,
      });
      await dispatch({
        type: 'update-pin-project-settings',
        payload: res?.data?.data,
      });
      notification.success({
        message: 'Project updated!',
        placement: 'bottomRight',
      });
      callback && callback();
    } catch (error) {
      const status = error?.response?.status;
      notification.error({
        message:
          status === 403
            ? 'You do not have access to this project'
            : error?.response?.data?.message || 'Something went wrong',
        placement: 'bottomRight',
      });
      dispatch({
        type: 'team-error',
        payload: error,
      });
    }
  };
};

export const fetchReceivedProjectById = (projectId) => async (dispatch) => {
  try {
    config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
    const res = await axios.get(`${baseUrl}/projects/${projectId}`, config);

    await dispatch({
      type: PROJECTS_ACTION_TYPES.ADD_PROJECT_REQUEST_SUCCESS, //'add-project',
      payload: res?.data?.data,
    });

    // notification.success({
    //   message: `You were added to the project: ${res?.data?.data?.title}`,
    //   placement: 'bottomRight',
    // });
  } catch (error) {}
};
